// ============================================================================
// useToast — lightweight component-level toast notification hook
// ----------------------------------------------------------------------------
// For components that don't receive setToast as a prop from App.jsx.
// Creates a self-contained toast state with auto-dismiss.
//
// Basic usage:
//   const { showToast, ToastBar } = useToast();
//   showToast('File saved successfully', 'success');
//   // Render <ToastBar /> at the bottom of your component
//
// Action usage (undo / view / etc.):
//   showToast('Photo removed', 'warning', {
//       action: { label: 'Undo', onClick: () => restorePhoto() },
//       duration: 6000,  // override default
//       title: 'Removed',
//   });
//
// v5.14.85 — backing render switched from inline createElement to the shared
// `<Toast>` + `<ToastStack>` component (src/components/ui/Toast.jsx +
// src/styles/qm-toast.css). Single canonical aesthetic across the app.
// ============================================================================

import { useState, useCallback, useRef, createElement } from 'react';
import { Toast, ToastStack } from '../components/ui/Toast';
import { shouldShowToast } from '../config/notification-policy';

const DEFAULT_DURATION = 4000;

/**
 * Lightweight toast hook for components that need their own notification system.
 * @param {number} [duration=4000] - Default auto-dismiss duration in ms
 * @param {{ suppressProgress?: boolean }} [options] - when suppressProgress is
 *   true, progress/completion toasts (info/success) are dropped per
 *   notification-policy.shouldShowToast(); failures + guard notices still show.
 *   Opt-in so this only affects the surfaces that want it (Stage 5 modal,
 *   Stage 2) and NOT the admin/training pages that share this hook.
 */
export function useToast(duration = DEFAULT_DURATION, options = {}) {
    const suppressProgress = !!options.suppressProgress;
    const [toast, setToast] = useState(null);

    const dismissToast = useCallback(() => {
        setToast(null);
    }, []);

    /**
     * @param {string} message
     * @param {'info'|'success'|'warning'|'error'|'neutral'} [type='info']
     * @param {{ action?: { label: string, onClick: () => void },
     *           secondaryAction?: { label: string, onClick: () => void },
     *           duration?: number,
     *           title?: string }} [opts]
     */
    const showToast = useCallback((message, type = 'info', opts = {}) => {
        // Progress/completion suppression (opt-in). Failures + guard notices
        // still pass shouldShowToast(); everything else info/success is dropped.
        if (suppressProgress && !shouldShowToast({ message, type })) return;
        const ttl = Number.isFinite(opts.duration) ? opts.duration : duration;
        setToast(prev => {
            // v5.37.13 dedup + v5.37.21 audit refinement: if the SAME message+type
            // is already showing AND the new emission landed within the dedup
            // window AND no action/title was supplied, return the existing toast
            // unchanged. Without this guard, any caller that fires the same toast
            // back-to-back (event loop, hot-rendered button) would re-trigger the
            // spring-entrance animation every render → "flashing" UX.
            //
            // v5.37.21 fix: pre-fix dedup was UNCONDITIONAL — a later call with
            // a longer `duration` was silently replaced by the earlier short
            // duration's remaining TTL. Now bounded to a 200ms debounce window
            // (typical re-render cadence is sub-ms; a 200ms window catches the
            // pathological re-fire without trapping legitimate later emissions).
            // After 200ms the same-message emission gets a fresh key + fresh TTL.
            const DEDUP_WINDOW_MS = 200;
            if (prev
                && prev.message === message
                && prev.type === type
                && !opts.action
                && !opts.secondaryAction
                && !opts.title
                && !prev.action
                && !prev.secondaryAction
                && !prev.title
                && (Date.now() - (prev.emittedAt || 0)) < DEDUP_WINDOW_MS) {
                return prev;
            }
            return {
                // key bump forces a fresh mount so the entrance animation re-runs
                // when distinct showToast calls land before the user dismisses.
                key: Date.now() + Math.random(),
                emittedAt: Date.now(),
                message,
                type,
                duration: ttl,
                action: opts.action || null,
                secondaryAction: opts.secondaryAction || null,
                title: opts.title || null,
            };
        });
    }, [duration, suppressProgress]);

    // Keep the renderer component identity stable while the toast itself is
    // unchanged. Defining a fresh component on every consumer render makes
    // React unmount/remount Toast, replaying its entrance animation and
    // resetting its timer for unrelated updates (Stage 1 file progress was the most
    // visible case). A genuinely new toast changes `toast`/key and still gets
    // the intended fresh mount.
    const ToastBar = useCallback(function ToastBar({ position = 'bottom-center' } = {}) {
        if (!toast) return null;
        return createElement(
            ToastStack,
            { position },
            createElement(Toast, {
                key: toast.key,
                type: toast.type,
                title: toast.title,
                message: toast.message,
                duration: toast.duration,
                action: toast.action,
                secondaryAction: toast.secondaryAction,
                onDismiss: dismissToast,
            })
        );
    }, [toast, dismissToast]);

    return { toast, showToast, dismissToast, ToastBar };
}
